"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdFormModal = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const dialog_1 = require("primereact/dialog");
const inputtext_1 = require("primereact/inputtext");
const inputnumber_1 = require("primereact/inputnumber");
const multiselect_1 = require("primereact/multiselect");
const AdMediaType_1 = require("../../../modules/advertising/enums/AdMediaType");
const useAdvertising_1 = require("../context/useAdvertising");
const useToast_1 = require("../../../hooks/toast/useToast");
const advertising_module_scss_1 = __importDefault(require("../advertising.module.scss"));
const IMAGE_MAX_BYTES = 2 * 1024 * 1024;
const VIDEO_MAX_BYTES = 20 * 1024 * 1024;
const DEFAULT_IMAGE_DURATION = 8;
const initialState = {
    title: '',
    mediaType: AdMediaType_1.AdMediaType.Image,
    source: 'file',
    file: null,
    url: '',
    duration: DEFAULT_IMAGE_DURATION,
    machineIds: [],
};
const isValidUrl = (value) => {
    try {
        const u = new URL(value);
        return u.protocol === 'http:' || u.protocol === 'https:';
    }
    catch {
        return false;
    }
};
/**
 * Modal de alta y edición de anuncios. Comparte formulario, validación y vista
 * previa para los dos modos; solo cambian el encabezado, el botón de envío y
 * la llamada (createAd vs updateAd). En edición carga el detalle del anuncio.
 */
const AdFormModal = ({ visible, mode, onHide, onSaved, adId, }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const { getMachinesSummary, getAdUploadUrl, createAd, getAd, updateAd } = (0, useAdvertising_1.useAdvertising)();
    const { showSuccess, showError } = (0, useToast_1.useToast)();
    const [form, setForm] = (0, react_1.useState)(initialState);
    const [errors, setErrors] = (0, react_1.useState)({});
    const [submitting, setSubmitting] = (0, react_1.useState)(false);
    const [machines, setMachines] = (0, react_1.useState)([]);
    const [machinesLoading, setMachinesLoading] = (0, react_1.useState)(false);
    const [loadingDetail, setLoadingDetail] = (0, react_1.useState)(false);
    const [previewUrl, setPreviewUrl] = (0, react_1.useState)('');
    const objectUrlRef = (0, react_1.useRef)(null);
    const isEdit = mode === 'edit';
    // Las etiquetas del formulario son comunes a ambos modos; solo el encabezado,
    // el botón de envío y los mensajes de resultado dependen de create/edit.
    const formKey = (key) => `advertising.ads.form.${key}`;
    const modeKey = (key) => `advertising.ads.${mode}.${key}`;
    const getMachinesSummaryRef = (0, react_1.useRef)(getMachinesSummary);
    const getAdRef = (0, react_1.useRef)(getAd);
    const showErrorRef = (0, react_1.useRef)(showError);
    const onHideRef = (0, react_1.useRef)(onHide);
    const tRef = (0, react_1.useRef)(t);
    (0, react_1.useEffect)(() => {
        getMachinesSummaryRef.current = getMachinesSummary;
        getAdRef.current = getAd;
        showErrorRef.current = showError;
        onHideRef.current = onHide;
        tRef.current = t;
    });
    (0, react_1.useEffect)(() => {
        if (!visible)
            return;
        setForm(initialState);
        setErrors({});
        setPreviewUrl('');
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        let cancelled = false;
        setMachinesLoading(true);
        getMachinesSummaryRef
            .current()
            .then((list) => {
            if (!cancelled)
                setMachines(list);
        })
            .finally(() => {
            if (!cancelled)
                setMachinesLoading(false);
        });
        // En edición se carga el detalle del anuncio para precargar el formulario.
        // El medio actual ya vive en una URL, así que el origen arranca en 'url'.
        if (mode === 'edit' && adId) {
            setLoadingDetail(true);
            getAdRef
                .current(adId)
                .then((detail) => {
                if (cancelled)
                    return;
                setForm({
                    title: detail.title,
                    mediaType: detail.mediaType,
                    source: 'url',
                    file: null,
                    url: detail.mediaUrl,
                    duration: detail.duration,
                    machineIds: detail.machineIds,
                });
                setPreviewUrl(isValidUrl(detail.mediaUrl) ? detail.mediaUrl : '');
            })
                .catch(() => {
                if (cancelled)
                    return;
                showErrorRef.current(tRef.current('advertising.ads.edit.loadError'));
                onHideRef.current();
            })
                .finally(() => {
                if (!cancelled)
                    setLoadingDetail(false);
            });
        }
        return () => {
            cancelled = true;
        };
    }, [visible, mode, adId]);
    (0, react_1.useEffect)(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, []);
    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };
    const clearError = (key) => setErrors((prev) => {
        if (!(key in prev))
            return prev;
        const next = { ...prev };
        delete next[key];
        return next;
    });
    const swapPreviewObjectUrl = (file) => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        if (!file) {
            setPreviewUrl('');
            return;
        }
        const url = URL.createObjectURL(file);
        objectUrlRef.current = url;
        setPreviewUrl(url);
    };
    const onMediaTypeChange = (mediaType) => {
        setField('mediaType', mediaType);
        setField('file', null);
        setField('duration', mediaType === AdMediaType_1.AdMediaType.Image ? DEFAULT_IMAGE_DURATION : 0);
        swapPreviewObjectUrl(null);
        clearError('file');
        clearError('duration');
    };
    const onSourceChange = (source) => {
        setField('source', source);
        setField('file', null);
        setField('url', '');
        swapPreviewObjectUrl(null);
        clearError('file');
        clearError('url');
    };
    const onFileChange = (file) => {
        setField('file', file);
        swapPreviewObjectUrl(file);
        clearError('file');
        if (file && form.mediaType === AdMediaType_1.AdMediaType.Video) {
            const video = document.createElement('video');
            const probeUrl = URL.createObjectURL(file);
            video.preload = 'metadata';
            video.src = probeUrl;
            video.onloadedmetadata = () => {
                const seconds = Math.max(1, Math.round(video.duration || 0));
                setField('duration', seconds);
                URL.revokeObjectURL(probeUrl);
            };
        }
    };
    const onUrlChange = (value) => {
        setField('url', value);
        clearError('url');
        if (form.source === 'url') {
            setPreviewUrl(isValidUrl(value) ? value : '');
        }
    };
    const validate = () => {
        const next = {};
        if (!form.title.trim())
            next.title = t(formKey('validation.titleRequired'));
        if (form.source === 'file') {
            if (!form.file) {
                next.file = t(formKey('validation.fileRequired'));
            }
            else {
                const isImage = form.mediaType === AdMediaType_1.AdMediaType.Image;
                const expectedPrefix = isImage ? 'image/' : 'video/';
                if (form.file.type && !form.file.type.startsWith(expectedPrefix)) {
                    next.file = t(formKey(isImage
                        ? 'validation.invalidImageType'
                        : 'validation.invalidVideoType'));
                }
                else if (isImage && form.file.size > IMAGE_MAX_BYTES) {
                    next.file = t(formKey('validation.imageTooLarge'));
                }
                else if (!isImage && form.file.size > VIDEO_MAX_BYTES) {
                    next.file = t(formKey('validation.videoTooLarge'));
                }
            }
        }
        else {
            if (!form.url.trim()) {
                next.url = t(formKey('validation.urlRequired'));
            }
            else if (!isValidUrl(form.url.trim())) {
                next.url = t(formKey('validation.urlInvalid'));
            }
        }
        if (form.mediaType === AdMediaType_1.AdMediaType.Image) {
            if (!form.duration || form.duration < 1 || form.duration > 60) {
                next.duration = t(formKey('validation.durationRange'));
            }
        }
        if (form.machineIds.length === 0) {
            next.machines = t(formKey('validation.machinesRequired'));
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };
    const handleSubmit = async () => {
        if (!validate() || submitting)
            return;
        setSubmitting(true);
        try {
            let uploadId;
            let mediaUrl;
            if (form.source === 'file' && form.file) {
                try {
                    const upload = await getAdUploadUrl(form.file);
                    uploadId = upload.uploadId;
                }
                catch {
                    showError(t(formKey('uploadError')));
                    setSubmitting(false);
                    return;
                }
            }
            else if (form.source === 'url') {
                mediaUrl = form.url.trim();
            }
            const payload = {
                title: form.title.trim(),
                mediaType: form.mediaType,
                duration: Math.max(1, Math.round(form.duration || 0)),
                machineIds: form.machineIds,
                ...(uploadId ? { uploadId } : {}),
                ...(mediaUrl ? { mediaUrl } : {}),
            };
            if (isEdit && adId) {
                await updateAd(adId, payload);
            }
            else {
                await createAd(payload);
            }
            showSuccess(t(modeKey('success')));
            onSaved();
            onHide();
        }
        catch {
            showError(t(modeKey('error')));
        }
        finally {
            setSubmitting(false);
        }
    };
    const machineOptions = (0, react_1.useMemo)(() => machines.map((m) => ({
        label: [m.name, m.siteName, m.code].filter(Boolean).join(' '),
        value: m.id,
        name: m.name,
        siteName: m.siteName,
        code: m.code,
    })), [machines]);
    const renderPreviewMedia = () => {
        if (!previewUrl) {
            return ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adPreviewPlaceholder, children: [(0, jsx_runtime_1.jsx)("i", { className: "pi pi-image", "aria-hidden": true }), (0, jsx_runtime_1.jsx)("span", { children: t(formKey('previewPlaceholder')) })] }));
        }
        if (form.mediaType === AdMediaType_1.AdMediaType.Video) {
            return ((0, jsx_runtime_1.jsx)("video", { src: previewUrl, className: advertising_module_scss_1.default.adPreviewMedia, controls: true, muted: true, playsInline: true }));
        }
        return ((0, jsx_runtime_1.jsx)("img", { src: previewUrl, alt: "", className: advertising_module_scss_1.default.adPreviewMedia, onError: () => setPreviewUrl('') }));
    };
    const header = ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adModalHeader, children: [(0, jsx_runtime_1.jsx)("h3", { children: t(modeKey('title')) }), (0, jsx_runtime_1.jsx)("p", { children: t(modeKey('subtitle')) })] }));
    const fileAccept = form.mediaType === AdMediaType_1.AdMediaType.Image ? 'image/*' : 'video/*';
    const fileHint = t(formKey(form.mediaType === AdMediaType_1.AdMediaType.Image
        ? 'fields.fileHintImage'
        : 'fields.fileHintVideo'));
    return ((0, jsx_runtime_1.jsxs)(dialog_1.Dialog, { visible: visible, onHide: onHide, header: header, draggable: false, dismissableMask: !submitting, closable: !submitting, className: advertising_module_scss_1.default.adModal, children: [isEdit && loadingDetail && ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adLoadingOverlay, children: [(0, jsx_runtime_1.jsx)("i", { className: "pi pi-spin pi-spinner", "aria-hidden": true }), (0, jsx_runtime_1.jsx)("span", { children: t('advertising.ads.edit.loading') })] })), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adModalBody, children: [(0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adPreviewCol, children: [(0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adPreviewLabel, children: [(0, jsx_runtime_1.jsx)("span", { children: t(formKey('preview')) }), (0, jsx_runtime_1.jsx)("span", { children: t(formKey('previewHint')) })] }), (0, jsx_runtime_1.jsx)("div", { className: advertising_module_scss_1.default.adPreviewFrame, children: renderPreviewMedia() })] }), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adFormCol, children: [(0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adFieldGroup, children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "adTitle", className: advertising_module_scss_1.default.adFieldLabel, children: t(formKey('fields.title')) }), (0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adInput, children: (0, jsx_runtime_1.jsx)(inputtext_1.InputText, { id: "adTitle", value: form.title, onChange: (e) => {
                                                setField('title', e.target.value);
                                                clearError('title');
                                            }, placeholder: t(formKey('fields.titlePlaceholder')) }) }), errors.title && ((0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adErrorText, children: errors.title }))] }), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adFieldGroup, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adFieldLabel, children: t(formKey('fields.mediaType')) }), (0, jsx_runtime_1.jsx)("div", { className: advertising_module_scss_1.default.adRadioRow, role: "radiogroup", children: [AdMediaType_1.AdMediaType.Image, AdMediaType_1.AdMediaType.Video].map((type) => {
                                            const isImage = type === AdMediaType_1.AdMediaType.Image;
                                            return ((0, jsx_runtime_1.jsxs)("label", { className: `${advertising_module_scss_1.default.adRadioOption} ${form.mediaType === type ? advertising_module_scss_1.default.adRadioOptionActive : ''}`, children: [(0, jsx_runtime_1.jsx)("input", { type: "radio", name: "mediaType", value: type, checked: form.mediaType === type, onChange: () => onMediaTypeChange(type) }), (0, jsx_runtime_1.jsx)("i", { className: `pi ${isImage ? 'pi-image' : 'pi-video'}`, "aria-hidden": true }), t(formKey(isImage
                                                        ? 'fields.mediaTypeImage'
                                                        : 'fields.mediaTypeVideo'))] }, type));
                                        }) })] }), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adFieldGroup, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adFieldLabel, children: t(formKey('fields.source')) }), (0, jsx_runtime_1.jsx)("div", { className: advertising_module_scss_1.default.adRadioRow, role: "radiogroup", children: ['file', 'url'].map((src) => ((0, jsx_runtime_1.jsxs)("label", { className: `${advertising_module_scss_1.default.adRadioOption} ${form.source === src ? advertising_module_scss_1.default.adRadioOptionActive : ''}`, children: [(0, jsx_runtime_1.jsx)("input", { type: "radio", name: "source", value: src, checked: form.source === src, onChange: () => onSourceChange(src) }), (0, jsx_runtime_1.jsx)("i", { className: `pi ${src === 'file' ? 'pi-upload' : 'pi-link'}`, "aria-hidden": true }), t(formKey(src === 'file' ? 'fields.sourceFile' : 'fields.sourceUrl'))] }, src))) })] }), form.source === 'file' ? ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adFieldGroup, children: [(0, jsx_runtime_1.jsxs)("label", { htmlFor: "adFile", className: advertising_module_scss_1.default.adFieldLabel, children: [(0, jsx_runtime_1.jsx)("span", { children: t(formKey('fields.file')) }), (0, jsx_runtime_1.jsxs)("span", { className: advertising_module_scss_1.default.adFieldHint, children: ["\u00B7 ", fileHint] })] }), (0, jsx_runtime_1.jsx)("input", { id: "adFile", type: "file", accept: fileAccept, className: advertising_module_scss_1.default.adFileInput, onChange: (e) => onFileChange(e.target.files?.[0] ?? null) }), errors.file && ((0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adErrorText, children: errors.file }))] })) : ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adFieldGroup, children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "adUrl", className: advertising_module_scss_1.default.adFieldLabel, children: t(formKey('fields.url')) }), (0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adInput, children: (0, jsx_runtime_1.jsx)(inputtext_1.InputText, { id: "adUrl", value: form.url, onChange: (e) => onUrlChange(e.target.value), placeholder: t(formKey('fields.urlPlaceholder')) }) }), errors.url && ((0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adErrorText, children: errors.url }))] })), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adFieldGroup, children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "adDuration", className: advertising_module_scss_1.default.adFieldLabel, children: t(formKey('fields.duration')) }), (0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adInput, children: (0, jsx_runtime_1.jsx)(inputnumber_1.InputNumber, { inputId: "adDuration", value: form.duration, onValueChange: (e) => {
                                                setField('duration', Number(e.value ?? 0));
                                                clearError('duration');
                                            }, min: 1, max: 60, useGrouping: false, disabled: form.mediaType === AdMediaType_1.AdMediaType.Video, showButtons: form.mediaType === AdMediaType_1.AdMediaType.Image, buttonLayout: "horizontal", incrementButtonIcon: "pi pi-plus", decrementButtonIcon: "pi pi-minus" }) }), (0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adFieldHelp, children: form.mediaType === AdMediaType_1.AdMediaType.Video
                                            ? t(formKey('fields.durationAuto'))
                                            : t(formKey('fields.durationHint')) }), errors.duration && ((0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adErrorText, children: errors.duration }))] }), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adFieldGroup, children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "adMachines", className: advertising_module_scss_1.default.adFieldLabel, children: t(formKey('fields.machines')) }), (0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adMultiselect, children: (0, jsx_runtime_1.jsx)(multiselect_1.MultiSelect, { inputId: "adMachines", value: form.machineIds, options: machineOptions, optionLabel: "label", optionValue: "value", filter: true, filterPlaceholder: t(formKey('fields.machinesFilterPlaceholder')), display: "chip", maxSelectedLabels: 3, placeholder: machinesLoading
                                                ? t(formKey('fields.machinesLoading'))
                                                : t(formKey('fields.machinesPlaceholder')), emptyMessage: t(formKey('fields.machinesEmpty')), emptyFilterMessage: t(formKey('fields.machinesEmpty')), onChange: (e) => {
                                                setField('machineIds', e.value);
                                                clearError('machines');
                                            }, disabled: machinesLoading, panelClassName: advertising_module_scss_1.default.adMachinesPanel, panelHeaderTemplate: (options) => ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adMachinesPanelHeader, children: [(0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adMachinesPanelTitle, children: [(0, jsx_runtime_1.jsx)("span", { children: t(formKey('fields.machinesPanelTitle')) }), (0, jsx_runtime_1.jsxs)("span", { className: advertising_module_scss_1.default.adMachinesPanelCount, children: [form.machineIds.length, "/", machineOptions.length] })] }), (0, jsx_runtime_1.jsx)("div", { className: advertising_module_scss_1.default.adMachinesPanelSearch, children: options.filterElement })] })), itemTemplate: (option) => ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adMachineOption, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adMachineAvatar, "aria-hidden": true, children: (0, jsx_runtime_1.jsx)("i", { className: "pi pi-server" }) }), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adMachineMain, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adMachineName, children: option.name }), (0, jsx_runtime_1.jsxs)("span", { className: advertising_module_scss_1.default.adMachineSite, children: [(0, jsx_runtime_1.jsx)("i", { className: "pi pi-map-marker", "aria-hidden": true }), option.siteName || '—'] })] }), option.code && ((0, jsx_runtime_1.jsxs)("span", { className: advertising_module_scss_1.default.adMachineCode, children: ["#", option.code] }))] })), selectedItemTemplate: (value) => {
                                                if (!value)
                                                    return null;
                                                const opt = machineOptions.find((o) => o.value === value);
                                                if (!opt)
                                                    return null;
                                                return ((0, jsx_runtime_1.jsxs)("span", { className: advertising_module_scss_1.default.adMachineChip, children: [(0, jsx_runtime_1.jsx)("i", { className: "pi pi-server", "aria-hidden": true }), (0, jsx_runtime_1.jsx)("span", { children: opt.name })] }));
                                            } }) }), form.machineIds.length > 0 && ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adMachinesSummary, children: [(0, jsx_runtime_1.jsxs)("span", { className: advertising_module_scss_1.default.adMachinesSummaryCount, children: [(0, jsx_runtime_1.jsx)("i", { className: "pi pi-check-circle", "aria-hidden": true }), t(formKey('fields.machinesSelected'), {
                                                        count: form.machineIds.length,
                                                    })] }), (0, jsx_runtime_1.jsxs)("button", { type: "button", className: advertising_module_scss_1.default.adMachinesClear, onClick: () => setField('machineIds', []), children: [(0, jsx_runtime_1.jsx)("i", { className: "pi pi-times", "aria-hidden": true }), t(formKey('fields.machinesClear'))] })] })), errors.machines && ((0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adErrorText, children: errors.machines }))] })] })] }), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adFooter, children: [(0, jsx_runtime_1.jsx)("button", { type: "button", className: advertising_module_scss_1.default.adCancelBtn, onClick: onHide, disabled: submitting, children: t(formKey('cancel')) }), (0, jsx_runtime_1.jsxs)("button", { type: "button", className: advertising_module_scss_1.default.adSubmitBtn, onClick: handleSubmit, disabled: submitting || loadingDetail, children: [submitting ? ((0, jsx_runtime_1.jsx)("i", { className: "pi pi-spin pi-spinner" })) : ((0, jsx_runtime_1.jsx)("i", { className: "pi pi-check" })), (0, jsx_runtime_1.jsx)("span", { children: t(modeKey('submit')) })] })] })] }));
};
exports.AdFormModal = AdFormModal;
